import { SyncSongDTO } from "./sync";

/** RFC4180-style field escaping: quote whenever the field contains the delimiter,
 *  a quote, or a newline, doubling any internal quotes. */
function escapeCsvField(value: string): string {
    if (/[",\n\r]/.test(value)) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
}

/**
 * CSV for songs a provider couldn't remove automatically (Apple Music has no removal
 * endpoint). Columns are Title/Artist/<provider> ID - the first two are what a human
 * (or the companion Shortcut's naive comma-split) matches on; the id is there for
 * anyone doing this by hand who wants an exact cross-check, since title/artist alone
 * can collide.
 */
export function buildManualRemovalsCsv(songs: SyncSongDTO[], provider = "Apple Music"): string {
    const header = `Title,Artist,${escapeCsvField(`${provider} ID`)}`;
    const rows = songs.map(song => [
        escapeCsvField(song.title || ""),
        escapeCsvField(song.artist || ""),
        escapeCsvField(song.externalTrackId || ""),
    ].join(","));
    return [header, ...rows].join("\r\n");
}

/** Triggers a browser download of `content` as a file named `filename`. */
export function downloadTextFile(filename: string, content: string, mimeType = "text/csv"): void {
    const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Deferred: revoking immediately can cut the download off in some browsers before
    // the click has actually been processed.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Sanitizes a playlist name into a safe-ish filename fragment. */
export function slugifyForFilename(name: string): string {
    return name.trim().replace(/[^a-zA-Z0-9-_]+/g, "-").replace(/^-+|-+$/g, "") || "playlist";
}
